import logo from '../assets/images/artie.png';
import { Card } from "@/components/ui/card";
import { ArrowLeft, Loader2, Copy, Mail } from "lucide-react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from "@/stores/authStore";
import { useOnboarding } from "@/context/OnboardingContext";
import { useArtieScore } from "@/hooks/use-artie-score";
import { useInventoryMode } from "@/hooks/useInventoryMode";
import { apiClient } from "@/lib/api";
import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import SpotlightContextCard from "@/components/SpotlightContextCard";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
export default function InitialAssessmentPage() {
    const navigate = useNavigate();
    const { user, checkAuth } = useAuthStore();
    const { data } = useOnboarding();
    const [searchParams] = useSearchParams();
    const [isNavigating, setIsNavigating] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [recipientName, setRecipientName] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const { toast } = useToast();
    const [scriptText, setScriptText] = useState<string | null>(null);
    const [isScriptLoading, setIsScriptLoading] = useState(false);

    // Get project ID from either onboarding context or URL query parameter
    // Priority: URL param > Context
    const projectId = searchParams.get('projectId') || data.projectId;

    // Both live upload flows END here — the signup wizard via LoadingV2 and the new-project wizard
    // via NewProjectLoading — so this is the last place a creator can be shown a score that is no
    // longer computed. The waiting screens redirect before reaching it, but guarding the terminus too
    // means a bookmark, a back-button, or a flow discovered later cannot land someone on an empty
    // assessment.
    const inventoryMode = useInventoryMode();

    const { scoreData, loading } = useArtieScore(projectId, false);

    // Check if user has any connected platforms
    const hasConnectedSocials = !!(user?.youtube_data || (user?.social_profiles && user.social_profiles.length > 0));

    useEffect(() => {
        if (inventoryMode) {
            navigate(
                projectId ? `/upload-thanks?projectId=${projectId}` : '/upload-thanks',
                { replace: true },
            );
        }
    }, [inventoryMode, projectId, navigate]);

    useEffect(() => {
        if (!projectId && !loading) {
            // Redirect if no project ID (user navigated directly)
            // For development/demo, we might want to stay
            // navigate('/onboarding-v2');
        }
    }, [projectId, loading, navigate]);

    // Mark onboarding as completed when user reaches Initial Assessment page
    useEffect(() => {
        const markOnboardingComplete = async () => {
            if (user && !user.onboarding_completed) {
                try {
                    await apiClient.updateCurrentUser({ onboarding_completed: true });
                    await checkAuth(); // Refresh user state
                } catch (error) {
                    console.error("Failed to mark onboarding as complete:", error);
                }
            }
        };

        markOnboardingComplete();
    }, [user, checkAuth]);

    useEffect(() => {
        const fetchScript = async () => {
            if (projectId) {
                setIsScriptLoading(true);
                try {
                    const scripts = await apiClient.getScriptsByProject(projectId);
                    if (scripts && scripts.length > 0) {
                        // Find the specific script that matches the score's script_id
                        const matchingScript = scripts.find(s => s.script_id === scoreData?.script_id);
                        if (matchingScript && matchingScript.extracted_text) {
                            setScriptText(matchingScript.extracted_text);
                        } else if (scripts.length > 0 && scripts[0].extracted_text) {
                            // Fallback to the first script if only one exists and has text
                            setScriptText(scripts[0].extracted_text);
                        }
                    }
                } catch (error) {
                    console.error("Failed to fetch script:", error);
                    toast({
                        title: "Could not load script",
                        description: "The script text could not be loaded for context.",
                        variant: "destructive",
                    });
                } finally {
                    setIsScriptLoading(false);
                }
            }
        };

        if (scoreData || projectId) {
            fetchScript();
        }
    }, [scoreData, projectId, toast]);

    // Redirect to loading if we have a project ID but no score data and we're loading
    // This ensures we don't show the result page until calculation is complete
    // NOTE: We only redirect if we are CERTAIN it's a fresh calculation that needs waiting
    // If we just came from there, or if we might have data soon, we should be careful.
    // For now, let's remove this auto-redirect to avoid the loop. The loader below is sufficient.
    /*
    useEffect(() => {
        if (loading && projectId && !scoreData) {
            // Update onboarding context with projectId before navigating
            // This is crucial because NewProjectLoading relies on context or we need to update it to read from URL
            navigate(`/projects/create/loading?projectId=${projectId}`);
        }
    }, [loading, projectId, scoreData, navigate]);
    */

    // Show loading state while redirecting or initial fetch
    if (loading && !scoreData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[oklch(1.0000_0_0)]">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 animate-spin text-[oklch(0.2686_0_0)]" />
                    <p className="text-[oklch(0.55_0_0)] font-ibm">Loading your results...</p>
                </div>
            </div>
        );
    }

    // If we have a project ID but no score data, show the page with fallback content
    // This allows the page to render even if score calculation hasn't completed yet

    // Format budget range for reach potential - handle null scoreData
    const reachLow = scoreData?.reach_analysis?.expected_reach_range?.low || 0;
    const reachHigh = scoreData?.reach_analysis?.expected_reach_range?.high || 0;
    const reachRange = `${(reachLow / 1000).toFixed(0)}K–${(reachHigh / 1000).toFixed(0)}K`;

    // Use actual PSV calculation from backend if available, otherwise show loading or fallback
    const calculateSponsorshipValue = () => {
        // If we're still loading and don't have complete data, show a default range
        if (loading || !scoreData) {
            return {
                low: 50000,
                high: 150000,
                isActualCalculation: false,
                isLoading: true
            };
        }

        // If we have the actual PSV calculation from backend, use it
        if (scoreData.psv_calculation?.psv_final) {
            const psvFinal = scoreData.psv_calculation.psv_final;
            // Create a reasonable range around the calculated value (±15%)
            const variance = 0.15;
            const low = psvFinal * (1 - variance);
            const high = psvFinal * (1 + variance);

            return {
                low: Math.max(Math.round(low), 500), // Respect minimum floor and round
                high: Math.min(Math.round(high), 750000), // Respect maximum ceiling and round
                isActualCalculation: true,
                psvFinal: psvFinal,
                isLoading: false
            };
        }

        // Fallback to simplified calculation if PSV not available but we have scoreData
        const brandRevenueTarget = scoreData.budget_target;
        let baseValue = 500000; // Default $500K

        if (brandRevenueTarget && brandRevenueTarget > 0) {
            baseValue = brandRevenueTarget;
        } else {
            const hasAnalytics = hasConnectedSocials || (scoreData.reach_analysis?.current_subscribers || 0) > 0;
            const expectedReach = scoreData.reach_analysis?.expected_reach_range?.high || 0;

            if (expectedReach > 1000000) {
                baseValue = 1200000;
            } else if (expectedReach > 500000) {
                baseValue = 800000;
            } else if (expectedReach > 100000) {
                baseValue = 600000;
            } else if (hasAnalytics) {
                baseValue = 400000;
            } else {
                baseValue = 250000;
            }
        }

        let multiplierLow = 0.5;
        let multiplierHigh = 1.0;

        // Apply various adjustments (keeping existing logic for fallback)
        if (scoreData.artie_score) {
            const score = scoreData.artie_score;
            if (score >= 80) {
                multiplierLow = 0.8;
                multiplierHigh = 1.5;
            } else if (score >= 60) {
                multiplierLow = 0.6;
                multiplierHigh = 1.2;
            } else if (score >= 40) {
                multiplierLow = 0.4;
                multiplierHigh = 0.9;
            } else {
                multiplierLow = 0.2;
                multiplierHigh = 0.6;
            }
        }

        if (scoreData.reach_status === 'green' && scoreData.brand_fit_status === 'green' && scoreData.production_readiness_status === 'green') {
            multiplierHigh *= 1.3;
        } else if (scoreData.reach_status === 'red' || scoreData.brand_fit_status === 'red') {
            multiplierLow *= 0.7;
            multiplierHigh *= 0.8;
        }

        if (scoreData.budget_viability === 'achievable') {
            multiplierLow *= 1.2;
            multiplierHigh *= 1.1;
        } else if (scoreData.budget_viability === 'unlikely') {
            multiplierLow *= 0.6;
            multiplierHigh *= 0.7;
        }

        const expectedReach = scoreData.reach_analysis?.expected_reach_range?.high || 0;
        if (expectedReach > 1000000) {
            multiplierHigh *= 1.2;
        } else if (expectedReach < 100000) {
            multiplierLow *= 0.8;
            multiplierHigh *= 0.9;
        }

        const accessibleBrands = scoreData.brand_fit_analysis?.accessible_brand_pool || 0;
        if (accessibleBrands > 100) {
            multiplierHigh *= 1.1;
        } else if (accessibleBrands < 20) {
            multiplierLow *= 0.8;
            multiplierHigh *= 0.9;
        }

        const premiumTier = scoreData.brand_fit_analysis?.premium_index?.tier;
        if (premiumTier === 'high') {
            multiplierHigh *= 1.2;
        } else if (premiumTier === 'low') {
            multiplierLow *= 0.8;
            multiplierHigh *= 0.9;
        }

        const finalLow = Math.max(50000, baseValue * multiplierLow);
        const finalHigh = Math.max(finalLow * 1.2, baseValue * multiplierHigh);

        return {
            low: finalLow,
            high: Math.min(finalHigh, baseValue * 3),
            isActualCalculation: false,
            isLoading: false
        };
    };

    const sponsorshipValue = calculateSponsorshipValue();

    // Smart formatting - use K for values under 1M, M for values 1M and above
    const formatValue = (value: number) => {
        if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'm';
        } else {
            return (value / 1000).toFixed(0) + 'k';
        }
    };

    const formattedValueLow = formatValue(sponsorshipValue.low);
    const formattedValueHigh = formatValue(sponsorshipValue.high);

    const handleCopyLink = () => {
        const baseUrl = window.location.origin;
        const referralCode = user?.referral_code;
        const url = referralCode ? `${baseUrl}/signup?referralCode=${referralCode}` : baseUrl;

        navigator.clipboard.writeText(url).then(() => {
            toast({
                title: "Link copied",
                description: "Share Artie with your friends!",
            });
        });
    };

    const handleEmailShare = () => {
        setShowEmailModal(true);
    };

    const handleSendEmail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!recipientName || !recipientEmail) {
            toast({
                title: "Missing information",
                description: "Please provide both name and email.",
                variant: "destructive",
            });
            return;
        }

        setIsSendingEmail(true);
        try {
            const baseUrl = window.location.origin;
            const referralCode = user?.referral_code;
            const url = referralCode ? `${baseUrl}/signup?referralCode=${referralCode}` : baseUrl;

            await apiClient.sendReferralEmail({
                recipient_name: recipientName,
                recipient_email: recipientEmail,
                referral_link: url
            });

            toast({
                title: "Email sent",
                description: `Invitation sent to ${recipientEmail}`,
            });

            setShowEmailModal(false);
            setRecipientName("");
            setRecipientEmail("");
        } catch (error) {
            console.error("Failed to send email:", error);
            toast({
                title: "Error",
                description: "Failed to send email. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsSendingEmail(false);
        }
    };

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">


            <main className="relative z-10 w-full flex flex-col items-center min-h-screen py-12 px-4 md:px-8">

                {/* Logo */}
                <div className="mb-8 relative">
                    <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                    <img
                        src={logo}
                        alt="Artie"
                        className="w-48 h-48 object-contain relative z-10"
                    />
                </div>

                <div className="flex flex-col items-center max-w-6xl w-full animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center text-[oklch(0.2686_0_0)] font-cabinet tracking-tight leading-tight">
                        There’s potential here!<br /> Let’s unpack it.
                    </h1>
                    <p className="text-lg text-[oklch(0.55_0_0)] text-center mb-12 max-w-xl font-ibm leading-relaxed">
                        Here’s what’s standing out, based on what I know so far.
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full mb-8">

                        {/* Potential Sponsorship Value */}
                        <Card className="p-6 bg-white shadow-sm border border-[oklch(0.92_0_0)] rounded-[16px] flex flex-col">
                            <h3 className="text-lg font-semibold mb-4">Potential Sponsorship Value</h3>

                            <div className="flex-1 flex flex-col justify-center">
                                <div className="text-4xl font-bold mb-2 tracking-tight">${formattedValueLow}–${formattedValueHigh}</div>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span className="text-xs font-medium text-green-700">Calculated from script analysis</span>
                                </div>
                                <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                    This range is based on our detailed analysis of your script's sponsorship opportunities and economic fundamentals.
                                </p>
                            </div>
                        </Card>

                        {/* What's Driving This Range */}
                        <Card className="p-6 bg-white shadow-sm border border-[oklch(0.92_0_0)] rounded-[16px] flex flex-col">
                            <h3 className="text-lg font-semibold mb-4">What's Driving This Range</h3>
                            <p className="text-sm text-[oklch(0.55_0_0)] mb-6">
                                I found signals in your project that suggest brand partnership potential.{" "}
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <span className="text-black font-medium cursor-pointer underline">Learn more</span>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80 p-4">
                                        <p className="text-sm text-[oklch(0.2686_0_0)] leading-relaxed">
                                            Our AI analyzes your script for potential Brand integrations. These are moments where the story naturally lends itself to a branded moment without distracting from the story.
                                        </p>
                                    </PopoverContent>
                                </Popover>
                            </p>

                            <div className="mb-4">
                                <div className="text-sm text-[oklch(0.55_0_0)] mb-2">Natural Integration Moments</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-4xl font-bold">
                                        {scoreData?.psv_calculation?.opportunities?.length ||
                                            scoreData?.brand_fit_analysis?.integration_opportunities?.natural_integration_points?.length ||
                                            0}
                                    </span>
                                    <span className="text-[oklch(0.55_0_0)]">moments</span>
                                </div>
                            </div>
                        </Card>

                    </div>

                    {/* Show spotlight card if we have opportunities from either data source */}
                    {(scoreData?.psv_calculation?.opportunities?.[0] || scoreData?.brand_fit_analysis?.integration_opportunities?.natural_integration_points?.[0]) && (
                        <div className="w-full mt-6">
                            <SpotlightContextCard
                                opportunity={
                                    scoreData.psv_calculation?.opportunities?.[0] ||
                                    scoreData.brand_fit_analysis.integration_opportunities.natural_integration_points[0]
                                }
                                scriptText={scriptText}
                                isScriptLoading={isScriptLoading}
                            />
                        </div>
                    )}

                    {/* Bottom Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full mt-8">

                        {/* What Happens Next */}
                        <Card className="p-8 bg-[#FAF9F6] shadow-sm border border-[oklch(0.92_0_0)] rounded-[24px] h-full">
                            <h3 className="font-semibold mb-6">What happens next</h3>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center shrink-0 border-4 border-[#FAF9F6]">
                                    <Loader2 className="w-5 h-5 text-white animate-spin" />
                                </div>
                                <div>
                                    <div className="font-medium text-[oklch(0.2686_0_0)] mb-2">A professional review is underway</div>
                                    <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                        We'll notify you in 3-5 business days with your full assessment and packaging options.
                                    </p>
                                </div>
                            </div>
                        </Card>

                        {/* Share Prompt */}
                        <Card className="p-8 bg-white shadow-sm border border-[oklch(0.92_0_0)] rounded-[24px] h-full">
                            <h3 className="font-semibold mb-4">Know someone who'd benefit from Artie?</h3>
                            <p className="text-sm text-[oklch(0.55_0_0)] mb-6">
                                Share Artie with friends and move up in the queue for your professional review.
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleCopyLink}
                                    className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-black px-4 py-3 rounded-xl transition-colors text-sm font-medium"
                                >
                                    <Copy className="w-4 h-4" /> Copy link
                                </button>
                                <button
                                    onClick={handleEmailShare}
                                    className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-black px-4 py-3 rounded-xl transition-colors text-sm font-medium"
                                >
                                    <Mail className="w-4 h-4" /> Share via email
                                </button>
                            </div>
                        </Card>

                    </div>

                    <div className="w-full mt-12 mb-8 flex justify-start">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Dashboard
                        </button>
                    </div>

                </div>
            </main>

            {/* Script Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-6xl h-[90vh] p-0">
                    <div className="flex flex-col h-full pt-6">
                        <div className="px-6 pb-4 border-b bg-white">
                            <h3 className="text-lg font-semibold">{data.scriptFileName || 'Script Preview'}</h3>
                        </div>
                        <div className="flex-1 overflow-hidden bg-gray-100">
                            {data.scriptId && (
                                <iframe
                                    src={`${import.meta.env.VITE_API_BASE_URL}/api/v1/scripts/${data.scriptId}/view#toolbar=0&navpanes=0&scrollbar=1`}
                                    className="w-full h-full border-0"
                                    title="Script Preview"
                                />
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Email Share Modal */}
            <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
                <DialogContent className="max-w-md p-6 bg-white rounded-2xl">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2 text-center">
                            <h3 className="text-xl font-bold font-cabinet">Share with a friend</h3>
                            <p className="text-sm text-gray-500 font-ibm">
                                Enter your friend's details to send them an invitation.
                            </p>
                        </div>

                        <form onSubmit={handleSendEmail} className="flex flex-col gap-4 mt-2">
                            <div className="flex flex-col gap-2">
                                <label htmlFor="name" className="text-sm font-medium text-gray-700">Friend's Name</label>
                                <input
                                    id="name"
                                    type="text"
                                    placeholder="e.g. Sarah Jones"
                                    value={recipientName}
                                    onChange={(e) => setRecipientName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                                    required
                                />
                            </div>

                            <div className="flex flex-col gap-2">
                                <label htmlFor="email" className="text-sm font-medium text-gray-700">Friend's Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="e.g. sarah@example.com"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                                    required
                                />
                            </div>

                            <div className="flex gap-3 mt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowEmailModal(false)}
                                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSendingEmail}
                                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSendingEmail ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="w-4 h-4" />
                                            Send Invite
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}